import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listTransactions } from "@/lib/api";
import { Modal } from "@/components/Modal";
import { fmtMoney } from "@/lib/money";

/* ════════════════════════════════════════════════════════════════════════
   StatementDrilldown — QuickBooks-style breakdown of any statement figure.

   Two levels:
   · aggregate rows (Income, Expenses, Net profit, Operating, Net cash flow)
     list the category lines that make the figure up; each is clickable;
   · a category × month shows the underlying ledger transactions, fetched
     with the existing /finance/transactions filters, with a footer total
     that reconciles to the clicked cell.
   ════════════════════════════════════════════════════════════════════════ */

/** How amounts are displayed: P&L category cells show raw magnitudes
 *  ("amount"); cash-flow cells are direction-signed ("signed"). */
export type DrillMode = "amount" | "signed";

export interface DrillLine {
  accountId: string;
  name: string;
  value: number;
  mode: DrillMode;
}

export type DrillState =
  | {
      kind: "category";
      accountId: string;
      label: string;
      /** YYYY-MM, or null for the year Total column. */
      month: string | null;
      year: number;
      value: number;
      mode: DrillMode;
    }
  | {
      kind: "aggregate";
      label: string;
      month: string | null;
      year: number;
      value: number;
      lines: DrillLine[];
    };

const monthTitle = (month: string | null, year: number) =>
  month
    ? new Date(`${month}-01`).toLocaleString("en-GB", {
        month: "long",
        year: "numeric",
      })
    : `${year} (full year)`;

function Amount({ value }: { value: number }) {
  return (
    <span
      className="font-semibold tabular-nums"
      style={{ color: value < 0 ? "var(--color-danger)" : undefined }}
    >
      {value < 0 ? "−" : ""}
      {fmtMoney(Math.abs(value))}
    </span>
  );
}

const lastDayOfMonth = (month: string) => {
  const [y, m] = month.split("-").map(Number);
  return `${month}-${String(new Date(y, m, 0).getDate()).padStart(2, "0")}`;
};

function TransactionList({
  state,
}: {
  state: Extract<DrillState, { kind: "category" }>;
}) {
  const from = state.month ? `${state.month}-01` : `${state.year}-01-01`;
  const to = state.month ? lastDayOfMonth(state.month) : `${state.year}-12-31`;
  const { data, isLoading } = useQuery({
    queryKey: ["fin-drill", state.accountId, from, to],
    queryFn: () => listTransactions({ from, to, accountId: state.accountId }),
  });

  if (isLoading) {
    return (
      <div className="grid place-items-center py-10 text-text-muted">
        <i className="fa-solid fa-circle-notch fa-spin" />
      </div>
    );
  }
  const txs = data ?? [];
  const signOf = (dir: string) =>
    state.mode === "signed" ? (dir === "in" ? 1 : -1) : 1;
  const total =
    Math.round(txs.reduce((s, t) => s + t.amount * signOf(t.direction), 0) * 100) /
    100;

  return (
    <div>
      {txs.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-muted">
          No transactions in this period.
        </p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[0.6rem] uppercase tracking-wider text-text-muted">
              <th className="py-2 pr-3 font-semibold">Date</th>
              <th className="py-2 pr-3 font-semibold">Description</th>
              <th className="py-2 pr-3 font-semibold">Paid via</th>
              <th className="py-2 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {txs.map((t) => (
              <tr key={t.id} className="border-b border-border-light">
                <td className="whitespace-nowrap py-2 pr-3 font-mono text-xs text-text-muted">
                  {new Date(t.date).toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  })}
                </td>
                <td className="max-w-[320px] truncate py-2 pr-3 text-text-secondary">
                  {t.description}
                </td>
                <td className="whitespace-nowrap py-2 pr-3 text-xs text-text-muted">
                  {t.paidAccount?.name ?? "—"}
                </td>
                <td className="whitespace-nowrap py-2 text-right tabular-nums">
                  <Amount value={t.amount * signOf(t.direction)} />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={3} className="py-2.5 pr-3 text-right font-semibold text-text-heading">
                Total
              </td>
              <td className="py-2.5 text-right">
                <Amount value={total} />
              </td>
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}

export function StatementDrilldown({
  initial,
  onClose,
}: {
  initial: DrillState;
  onClose: () => void;
}) {
  // Descending from an aggregate into a category pushes onto a stack so the
  // header's back button can return to the aggregate view.
  const [stack, setStack] = useState<DrillState[]>([initial]);
  const state = stack[stack.length - 1];

  const title = `${state.label} — ${monthTitle(state.month, state.year)}`;

  return (
    <Modal open onClose={onClose} title={title} wide>
      {stack.length > 1 && (
        <button
          onClick={() => setStack((s) => s.slice(0, -1))}
          className="mb-3 inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline"
        >
          <i className="fa-solid fa-arrow-left text-[10px]" />
          Back to {stack[stack.length - 2].label}
        </button>
      )}

      {state.kind === "category" ? (
        <TransactionList state={state} />
      ) : (
        <AggregateBreakdown
          state={state}
          onDescend={(line) =>
            setStack((s) => [
              ...s,
              {
                kind: "category",
                accountId: line.accountId,
                label: line.name,
                month: state.month,
                year: state.year,
                value: line.value,
                mode: line.mode,
              },
            ])
          }
        />
      )}
    </Modal>
  );
}

function AggregateBreakdown({
  state,
  onDescend,
}: {
  state: Extract<DrillState, { kind: "aggregate" }>;
  onDescend: (line: DrillLine) => void;
}) {
  const lines = state.lines.filter((l) => Math.abs(l.value) >= 0.005);
  const total =
    Math.round(lines.reduce((s, l) => s + l.value, 0) * 100) / 100;
  return (
    <div>
      {lines.length === 0 ? (
        <p className="py-8 text-center text-sm text-text-muted">
          Nothing recorded in this period.
        </p>
      ) : (
        <table className="w-full text-sm">
          <tbody>
            {lines.map((l) => (
              <tr
                key={l.accountId}
                onClick={() => onDescend(l)}
                className="group cursor-pointer border-b border-border-light hover:bg-bg-card-hover"
                title="View transactions"
              >
                <td className="py-2.5 pr-3 text-text-secondary group-hover:text-text-heading">
                  {l.name}
                </td>
                <td className="py-2.5 text-right tabular-nums">
                  <Amount value={l.value} />
                </td>
                <td className="w-6 py-2.5 text-right text-text-muted">
                  <i className="fa-solid fa-chevron-right text-[10px] opacity-0 group-hover:opacity-100" />
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="py-2.5 pr-3 font-semibold text-text-heading">Total</td>
              <td className="py-2.5 text-right">
                <Amount value={total} />
              </td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  );
}
